import {
	OrganizationAssignmentType,
	OrganizationLocationType,
	OrganizationUnitStatus,
	SOURCE_SYSTEM_PATTERN,
} from '@today/types'
import { z } from 'zod'

// Organization (TO-411) request schemas. `tenantId` is never part of an input —
// it comes from the DB-resolved principal (ADR-0034). Every object is `.strict()`
// so client-supplied hierarchy data (depth, ancestors, path…) is refused rather
// than silently ignored (REQ-006).

/** Tenant business identifier: opaque, case-sensitive, leading zeros kept. */
export const orgCodeSchema = z
	.string()
	.regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/, 'code must be 1–64 chars of A-Z a-z 0-9 . _ - starting alphanumeric')

const nameSchema = z.string().trim().min(1).max(200)

const METADATA_MAX_BYTES = 16 * 1024

/** A JSON object (never an array) whose serialization is ≤ 16 KiB. */
export const orgMetadataSchema = z
	.record(z.string(), z.unknown())
	.refine((value) => Buffer.byteLength(JSON.stringify(value), 'utf8') <= METADATA_MAX_BYTES, {
		message: `metadata must serialize to at most ${METADATA_MAX_BYTES} bytes`,
	})

const isIanaTimezone = (value: string): boolean => {
	try {
		// Validation only: the constructor throws (RangeError) on an unknown IANA
		// zone. Locale is irrelevant here — we never format, just probe validity.
		new Intl.DateTimeFormat(undefined, { timeZone: value })
		return true
	} catch {
		return false
	}
}

const timezoneSchema = z.string().max(64).refine(isIanaTimezone, { message: 'timezone must be an IANA zone name' })
const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/, 'countryCode must be ISO 3166-1 alpha-2 uppercase')
const timestampSchema = z.iso.datetime({ offset: true }).transform((value) => new Date(value))

// ─── Unit types ───────────────────────────────────────────────────────────────

export const organizationTypeCreateSchema = z
	.object({
		code: orgCodeSchema,
		name: nameSchema,
		isPhysical: z.boolean().default(false),
		canHaveUsers: z.boolean().default(true),
		sortOrder: z.number().int().min(0).max(10_000).default(0),
		metadata: orgMetadataSchema.default({}),
		allowedParentTypeCodes: z.array(orgCodeSchema).max(50).nullable().default(null),
	})
	.strict()

/** `code` is accepted only so an attempted change maps to `ORG_UNIT_TYPE_CODE_IMMUTABLE`. */
export const organizationTypeUpdateSchema = z
	.object({
		code: orgCodeSchema.optional(),
		name: nameSchema.optional(),
		isPhysical: z.boolean().optional(),
		canHaveUsers: z.boolean().optional(),
		sortOrder: z.number().int().min(0).max(10_000).optional(),
		metadata: orgMetadataSchema.optional(),
		allowedParentTypeCodes: z.array(orgCodeSchema).max(50).nullable().optional(),
	})
	.strict()

// ─── Units ────────────────────────────────────────────────────────────────────

const unitDescriptiveFields = {
	shortName: z.string().trim().min(1).max(100).nullable().optional(),
	externalId: z.string().min(1).max(128).nullable().optional(),
	status: z.enum(OrganizationUnitStatus).optional(),
	isPhysical: z.boolean().optional(),
	institutionCode: z.string().min(1).max(32).nullable().optional(),
	countryCode: countryCodeSchema.nullable().optional(),
	timezone: timezoneSchema.nullable().optional(),
	metadata: orgMetadataSchema.optional(),
	validFrom: timestampSchema.nullable().optional(),
	validTo: timestampSchema.nullable().optional(),
}

export const organizationUnitCreateSchema = z
	.object({
		code: orgCodeSchema,
		name: nameSchema,
		typeCode: orgCodeSchema,
		parentCode: orgCodeSchema.nullable().optional(),
		...unitDescriptiveFields,
	})
	.strict()

/**
 * `code` / `parentCode` are accepted as opaque values only so their presence maps
 * to `ORG_UNIT_IMMUTABLE_FIELD` (a move is its own operation), not to a generic
 * unknown-property failure.
 */
export const organizationUnitUpdateSchema = z
	.object({
		code: z.unknown().optional(),
		parentCode: z.unknown().optional(),
		name: nameSchema.optional(),
		typeCode: orgCodeSchema.optional(),
		...unitDescriptiveFields,
	})
	.strict()

export const organizationUnitMoveSchema = z.object({ parentCode: orgCodeSchema.nullable() }).strict()

// ─── Locations (TO-387) ───────────────────────────────────────────────────────

const text = (max: number) => z.string().trim().min(1).max(max)

/** Kept exactly as given (leading zeros are significant, e.g. `01001`). */
const postalCodeSchema = z
	.string()
	.regex(
		/^[A-Za-z0-9][A-Za-z0-9 -]{0,15}$/,
		'postalCode must be 1–16 chars of A-Z a-z 0-9 space - starting alphanumeric',
	)
const phoneSchema = z
	.string()
	.regex(/^\+?[0-9][0-9 ().-]{2,31}$/, 'phone must be 3–32 chars of digits, space ( ) . - with optional leading +')

/** The optional location fields — `null` clears, omitted means `null` on create. */
const locationOptionalFields = {
	addressLine2: text(200).nullable().optional(),
	postalCode: postalCodeSchema.nullable().optional(),
	province: text(100).nullable().optional(),
	region: text(100).nullable().optional(),
	latitude: z.number().min(-90).max(90).nullable().optional(),
	longitude: z.number().min(-180).max(180).nullable().optional(),
	phone: phoneSchema.nullable().optional(),
	email: z.email().max(254).nullable().optional(),
}

// `unitId`, `id` and timestamps are refused by `.strict()`: a location's unit is
// the one in the path and never changes.
export const organizationLocationCreateSchema = z
	.object({
		locationType: z.enum(OrganizationLocationType),
		addressLine1: text(200),
		city: text(100),
		countryCode: countryCodeSchema,
		...locationOptionalFields,
	})
	.strict()

/** Any non-empty subset of the create fields; required fields cannot be cleared. */
export const organizationLocationUpdateSchema = z
	.object({
		locationType: z.enum(OrganizationLocationType).optional(),
		addressLine1: text(200).optional(),
		city: text(100).optional(),
		countryCode: countryCodeSchema.optional(),
		...locationOptionalFields,
	})
	.strict()
	.refine((value) => Object.keys(value).length > 0, { message: 'at least one field is required' })

// ─── User ↔ unit assignments (TO-412) ───────────────────────────────────────────

/**
 * `userId` is an opaque tenant-local reference; the unit is named by code. Window
 * rules that depend on "now" (REQ-001) are checked by the service, not here.
 */
export const organizationAssignmentCreateSchema = z
	.object({
		userId: z.uuid(),
		unitCode: orgCodeSchema,
		/** The unit's source system (TO-518); absent or null = an unsourced unit. */
		unitSourceSystem: z.string().regex(SOURCE_SYSTEM_PATTERN).nullable().optional(),
		assignmentType: z.enum(OrganizationAssignmentType),
		isPrimary: z.boolean().optional(),
		validFrom: timestampSchema.optional(),
		validTo: timestampSchema.nullable().optional(),
	})
	.strict()

/** End / make-primary take no input: an absent body or `{}` only. */
export const organizationAssignmentEmptyBodySchema = z.object({}).strict().optional()

/** Pagination for organization list reads — out-of-range values are refused, not clamped. */
export const orgPageQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	limit: z.coerce.number().int().min(1).max(100).default(20),
})

export type OrganizationTypeCreateInput = z.infer<typeof organizationTypeCreateSchema>
export type OrganizationTypeUpdateInput = z.infer<typeof organizationTypeUpdateSchema>
export type OrganizationUnitCreateInput = z.infer<typeof organizationUnitCreateSchema>
export type OrganizationUnitUpdateInput = z.infer<typeof organizationUnitUpdateSchema>
export type OrganizationUnitMoveInput = z.infer<typeof organizationUnitMoveSchema>
export type OrgPageQuery = z.infer<typeof orgPageQuerySchema>
export type OrganizationLocationCreateInput = z.infer<typeof organizationLocationCreateSchema>
export type OrganizationLocationUpdateInput = z.infer<typeof organizationLocationUpdateSchema>
export type OrganizationAssignmentCreateInput = z.infer<typeof organizationAssignmentCreateSchema>
